/**
 * `mileageSource` / `fuelSource` from API may be a string (cachedData key) or an option-shaped object.
 */
function normalizeTelemetrySourceKey(sourceType) {
  if (sourceType == null || sourceType === '') return null;
  if (typeof sourceType === 'string') return sourceType;
  if (typeof sourceType === 'object') {
    return sourceType.value ?? sourceType.name ?? sourceType.mileageSource ?? null;
  }
  return null;
}

const ANALOG_FUEL_SOURCES = new Set(['analog1', 'analog2', 'analog3']);

/** Ingest writes normalised volts under cachedData.analog (telemetrySnapshot shape). */
const ANALOG_VOLTAGE_FIELD_BY_SOURCE = {
  analog1: 'analog1V',
  analog2: 'analog2V',
  analog3: 'analog3V',
};

export function isAnalogFuelSource(fuelSource) {
  const key = normalizeTelemetrySourceKey(fuelSource);
  return key != null && ANALOG_FUEL_SOURCES.has(key);
}

function readAnalogVoltage(cachedData, fuelSource) {
  const sourceKey = normalizeTelemetrySourceKey(fuelSource);
  const voltageField = ANALOG_VOLTAGE_FIELD_BY_SOURCE[sourceKey];
  if (!voltageField) {
    return null;
  }

  const analogBucket = cachedData.analog;
  if (analogBucket && analogBucket[voltageField] != null && analogBucket[voltageField] !== '') {
    return analogBucket[voltageField];
  }

  // Fallback if a future engine nests values under cachedData.analog2, etc.
  const sourceBucket = cachedData[sourceKey];
  if (sourceBucket) {
    if (sourceBucket.fuelLevelVoltage != null && sourceBucket.fuelLevelVoltage !== '') {
      return sourceBucket.fuelLevelVoltage;
    }
    if (sourceBucket[voltageField] != null && sourceBucket[voltageField] !== '') {
      return sourceBucket[voltageField];
    }
  }

  return null;
}

/**
 * Read a scalar telemetry field; preserve numeric zero (0 is valid fuel/mileage).
 */
function readTelemetryField(bucket, key) {
  if (!bucket || !Object.prototype.hasOwnProperty.call(bucket, key)) {
    return null;
  }
  const value = bucket[key];
  if (value == null || value === '') {
    return null;
  }
  return value;
}

/**
 * Read a field from live device telemetry `cachedData` (same logic as Live map asset card).
 * @param {object} data - Marker / payload row with `cachedData`
 * @param {string|object} sourceType - e.g. `mileageSource` from APIGetDevices
 * @param {string} key - e.g. `mileageKm`
 */
export function getTelemetrySourceData(data, sourceType, key) {
  const cachedData = data?.cachedData || {};
  const primary = normalizeTelemetrySourceKey(sourceType);
  if (primary && cachedData[primary]) {
    return readTelemetryField(cachedData[primary], key);
  }
  return null;
}

/**
 * Fuel telemetry for asset cards — reads perc/litre/voltage from the device fuelSource bucket.
 * Analog sources: ingest stores volts on cachedData.analog.analog{n}V (e.g. analog2V).
 */
export function getFuelLevelTelemetry(data, fuelSource) {
  const sourceKey = normalizeTelemetrySourceKey(fuelSource);
  const cachedData = data?.cachedData || {};

  let fuelLevelVoltage = null;
  if (isAnalogFuelSource(sourceKey)) {
    fuelLevelVoltage = readAnalogVoltage(cachedData, sourceKey);
  } else {
    fuelLevelVoltage = getTelemetrySourceData(data, fuelSource, 'fuelLevelVoltage');
  }

  return {
    fuelLevelPerc: getTelemetrySourceData(data, fuelSource, 'fuelLevelPerc'),
    fuelLevelLitre: getTelemetrySourceData(data, fuelSource, 'fuelLevelLitre'),
    fuelLevelVoltage,
  };
}

/**
 * Total mileage (km) from live payload using the device mileageSource configuration.
 * Matches Live map asset card "Total Mileage".
 */
export function resolveDeviceTotalMileageKm(payloadRow, mileageSource) {
  if (!payloadRow) return null;
  const fromSource = getTelemetrySourceData(payloadRow, mileageSource, 'mileageKm');
  const raw = fromSource ?? payloadRow.odometer;
  if (raw == null || raw === '') return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? Math.round(numeric) : null;
}
